import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DndContext } from '@dnd-kit/core';
import { Card, Badge, Button, Spinner } from '@components/ui';
import TaskCard from '@components/features/TaskCard';
import BurndownChart from '@components/features/BurndownChart';
import { useSprint, useTasks, useTaskStatuses } from '@hooks';
import { STATUS_CONFIG } from '@config/constants';
import { useUIStore, useThemeStore } from '@store';

const sprintStatusColors = {
  PLANNED: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Planificaci\u00f3n' },
  ACTIVE: { bg: 'bg-emerald-100', text: 'text-emerald-600', label: 'Activo' },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Completado' },
};

const DEFAULT_COLUMNS = ['backlog', 'todo', 'progress', 'review', 'done'];

const formatDateDisplay = (dateValue) => {
  if (!dateValue) return '';
  const dateStr = typeof dateValue === 'string' && dateValue.includes('T')
    ? dateValue.split('T')[0]
    : dateValue;
  const [year, month, day] = dateStr.split('-');
  return `${parseInt(day)}/${parseInt(month)}/${year}`;
};

export default function SprintDetailView({ sprintId, projectId }) {
  const setActiveView = useUIStore((state) => state.setActiveView);
  const setSelectedSprintId = useUIStore((state) => state.setSelectedSprintId);
  const setSelectedTaskId = useUIStore((state) => state.setSelectedTaskId);
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const { data: sprintData, isLoading: sprintLoading } = useSprint(sprintId);
  const sprint = sprintData?.data;

  const { data: tasksData, isLoading: tasksLoading } = useTasks(
    { projectId, sprintId },
    { enabled: !!projectId && !!sprintId }
  );
  const tasks = tasksData?.data || [];

  const { data: taskStatusesData } = useTaskStatuses(projectId);
  const taskStatuses = taskStatusesData?.data || [];

  const columns = useMemo(() => {
    if (taskStatuses && taskStatuses.length > 0) {
      return taskStatuses.map((s) => ({
        id: s.name,
        label: s.label,
        color: s.color,
      }));
    }
    return DEFAULT_COLUMNS.map((status) => ({
      id: status,
      label: STATUS_CONFIG[status]?.label || status,
      color: STATUS_CONFIG[status]?.color || '#64748b',
    }));
  }, [taskStatuses]);

  const tasksByStatus = useMemo(() => {
    const grouped = {};
    columns.forEach((col) => {
      grouped[col.id] = [];
    });
    tasks.forEach((task) => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      } else if (grouped['backlog']) {
        grouped['backlog'].push(task);
      }
    });
    // Sort within each column by order
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return grouped;
  }, [tasks, columns]);

  const metrics = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter((t) => t.status === 'done').length;
    const totalPoints = tasks.reduce((s, t) => s + (t.points || 0), 0);
    const donePoints = tasks.filter((t) => t.status === 'done').reduce((s, t) => s + (t.points || 0), 0);
    const progress = totalPoints > 0
      ? Math.round((donePoints / totalPoints) * 100)
      : total > 0
        ? Math.round((done / total) * 100)
        : 0;
    return { total, done, totalPoints, donePoints, progress };
  }, [tasks]);

  const handleBack = () => {
    setSelectedSprintId(null);
    setActiveView('sprints');
  };

  const handleTaskClick = (task) => {
    setSelectedTaskId(task.id);
    setActiveView('task-detail');
  };

  if (sprintLoading || tasksLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!sprint) {
    return (
      <div className="p-8 text-center">
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Sprint no encontrado</p>
        <Button variant="secondary" onClick={handleBack} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  const statusStyle = sprintStatusColors[sprint.status] || sprintStatusColors.PLANNED;

  const titleClass = isDark ? 'text-slate-100' : 'text-slate-800';
  const subtitleClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const metaLabelClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const metaValueClass = isDark ? 'text-slate-100' : 'text-slate-800';
  const columnBgClass = isDark ? 'bg-slate-800/60' : 'bg-slate-50';
  const columnTitleClass = isDark ? 'text-slate-200' : 'text-slate-700';

  const metricCards = [
    { label: 'Total Tareas', value: metrics.total, icon: '\ud83d\udccb', color: '#667eea' },
    { label: 'Completadas', value: metrics.done, icon: '\u2705', color: '#10b981' },
    { label: 'Story Points', value: `${metrics.donePoints} / ${metrics.totalPoints}`, icon: '\ud83c\udfaf', color: '#8b5cf6' },
    { label: 'Progreso', value: `${metrics.progress}%`, icon: '\ud83d\udcc8', color: '#f59e0b' },
  ];

  return (
    <div className="pb-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleBack} className="!px-2">
            {'\u2190'} Volver
          </Button>
          <h1 className={`text-xl font-bold ${titleClass}`}>{sprint.name}</h1>
          <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>{statusStyle.label}</Badge>
        </div>
        {(sprint.startDate || sprint.endDate) && (
          <span className={`text-sm ${subtitleClass}`}>
            {sprint.startDate && formatDateDisplay(sprint.startDate)} - {sprint.endDate && formatDateDisplay(sprint.endDate)}
          </span>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {metricCards.map((stat, i) => (
          <Card key={i} padding="md">
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-xs ${metaLabelClass} mb-1`}>{stat.label}</p>
                <h3 className={`text-2xl font-bold ${metaValueClass}`}>{stat.value}</h3>
              </div>
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                style={{ backgroundColor: stat.color + (isDark ? '25' : '15') }}
              >
                {stat.icon}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Goal */}
      {sprint.goal && (
        <Card className="mb-6" padding="md">
          <h3 className={`text-sm font-semibold ${columnTitleClass} mb-1`}>Objetivo del Sprint</h3>
          <p className={`text-sm ${subtitleClass}`}>{sprint.goal}</p>
        </Card>
      )}

      {/* Progress Bar */}
      <div className="mb-6">
        <div className={`flex justify-between text-xs ${subtitleClass} mb-1`}>
          <span>Progreso</span>
          <span>{metrics.donePoints} / {metrics.totalPoints} pts ({metrics.progress}%)</span>
        </div>
        <div className={`w-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-full h-2.5`}>
          <div
            className="bg-indigo-500 h-2.5 rounded-full transition-all"
            style={{ width: `${metrics.progress}%` }}
          />
        </div>
      </div>

      {/* Task Columns - Mini Kanban (read-only) */}
      <h2 className={`text-lg font-semibold ${titleClass} mb-4`}>Tareas del Sprint</h2>
      {tasks.length === 0 ? (
        <Card className="text-center py-12">
          <p className={subtitleClass}>No hay tareas asignadas a este sprint</p>
        </Card>
      ) : (
        <DndContext>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {columns.map((column) => {
              const columnTasks = tasksByStatus[column.id] || [];
              return (
                <div
                  key={column.id}
                  className={`min-w-[280px] flex-shrink-0 ${columnBgClass} rounded-2xl p-4`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: column.color }}
                      />
                      <h3 className={`text-sm font-bold ${columnTitleClass}`}>{column.label}</h3>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded-lg text-xs font-semibold"
                      style={{ backgroundColor: column.color + '20', color: column.color }}
                    >
                      {columnTasks.length}
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    {columnTasks.map((task) => (
                      <TaskCard key={task.id} task={task} onClick={handleTaskClick} />
                    ))}
                    {columnTasks.length === 0 && (
                      <p className={`text-xs ${subtitleClass} text-center py-4`}>Sin tareas</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DndContext>
      )}

      {/* Burndown Chart for active sprints */}
      {sprint.status === 'ACTIVE' && (
        <div className="mt-6">
          <BurndownChart sprintId={sprintId} />
        </div>
      )}
    </div>
  );
}

SprintDetailView.propTypes = {
  sprintId: PropTypes.number.isRequired,
  projectId: PropTypes.number,
};
